// ---------------------------------------------------------------------------
// DeviceConnection — transport-agnostic base class
//
// Subclasses must implement:
//   run(command, data)  — send a command and return a Promise<string>
//   disconnect()        — close the underlying transport
//
// The named command methods declared here delegate to run() so that callers
// never need to know the raw command strings.
// ---------------------------------------------------------------------------

export default class DeviceConnection {
  // Subclasses override these two:
  run(command, data = null) {
    throw new Error('DeviceConnection.run() must be implemented by subclass');
  }

  async disconnect() {
    throw new Error('DeviceConnection.disconnect() must be implemented by subclass');
  }

  // ---- Named command methods ----

  // Returns the raw VERSION response string (e.g. "MIDULATE 0.5.0").
  async version() {
    return this.run('VERSION');
  }

  // Downloads the device config and returns the parsed JSON object.
  async download() {
    const response = await this.run('DOWNLOAD');
    return JSON.parse(response);
  }

  // Uploads the full config object to the device.
  async uploadConfig(config) {
    return this.run('UPLOAD_CONFIG', config);
  }

  // Switches the device to the patch identified by uuid.
  async setPatch(uuid) {
    return this.run('SET_PATCH', uuid);
  }

  // Replaces the live effects array atomically.
  async setEffects(effects) {
    return this.run('SET_EFFECTS', effects);
  }

  // Mutes the effects at the given indices (array of integers, e.g. [0, 2]).
  async setMutedEffects(indices) {
    return this.run('SET_MUTED_EFFECTS', indices);
  }

  // Prepares the device for a firmware update.
  async prepareFirmwareUpdate() {
    return this.run('PREPARE_FIRMWARE_UPDATE');
  }

  // Returns the current WiFi status as a parsed object:
  //   { connected: bool, ip: string|null, ssid: string|null, mode: string|null, hostname: string|null }
  // Only supported over USB — HttpDeviceConnection returns null.
  async getWifiStatus() {
    return null;
  }

  // Attempts to reconnect to the station-mode WiFi network using the supplied
  // credentials.  Returns the new WiFi status object on success (same shape as
  // getWifiStatus).  Only supported over USB — HttpDeviceConnection returns null.
  // eslint-disable-next-line no-unused-vars
  async wifiReconnect(ssid, password) {
    return null;
  }

  // Returns the capabilities object from the device:
  //   { capabilities_version: 1, capabilities: { ... } }
  // Falls back to empty capabilities for pre-GET_CAPABILITIES firmware.
  async getCapabilities() {
    return { capabilities_version: 1, capabilities: {} };
  }
}
